export class Show {
  constructor({
    id,
    band_name,
    venue_name,
    show_date,
    show_time = null,
    ticket_price = null,
    age_limit = null,
    description,
    ticket_url = null,
    venue_address = null,
    city,
    state,
    latitude = null,
    longitude = null,
    created_at,
    genre,
    source = 'manual',
    poster,
    approval_status = 'approved',
    approval_count = 0,
    required_approvals = 0,
    approved_by = [],
    flagged_as_spam = false,
    report_count = 0,
    reports = [],
    reported_by = [],
    is_hidden = false,
    hidden_at = null,
    total_reports = 0,
    thanked_by = []
  }) {
    this.id = id;
    this.band_name = band_name;
    this.venue_name = venue_name;
    this.show_date = show_date;
    this.show_time = show_time;
    this.ticket_price = ticket_price;
    this.age_limit = age_limit;
    this.description = description;
    this.ticket_url = ticket_url;
    this.venue_address = venue_address;
    this.city = city;
    this.state = state;
    this.latitude = latitude;
    this.longitude = longitude;
    this.created_at = created_at;
    this.genre = genre;
    this.source = source;
    this.poster = poster;
    this.approval_status = approval_status;
    this.approval_count = approval_count;
    this.required_approvals = required_approvals;
    this.approved_by = approved_by;
    this.flagged_as_spam = flagged_as_spam;
    this.report_count = report_count;
    this.reports = reports;
    this.reported_by = reported_by;
    this.is_hidden = is_hidden;
    this.hidden_at = hidden_at;
    this.total_reports = total_reports;
    this.thanked_by = thanked_by;
  }

  static fromJson(json) {
    // Helper function to parse date fields safely
    const parseDate = (dateValue) => {
      if (typeof dateValue === 'string') {
        try {
          return new Date(dateValue);
        } catch (error) {
          console.error('Error parsing date string:', dateValue);
          return new Date();
        }
      } else if (dateValue && typeof dateValue === 'object' && dateValue.toDate) {
        // Handle Firebase Timestamp objects (for backward compatibility)
        return dateValue.toDate();
      } else if (dateValue instanceof Date) {
        return dateValue;
      } else {
        if (dateValue != null) {
          console.warn('Unknown date type:', typeof dateValue);
        }
        return new Date();
      }
    };

    return new Show({
      id: json.id,
      band_name: json.band_name || '',
      venue_name: json.venue_name || '',
      show_date: parseDate(json.show_date),
      show_time: json.show_time,
      ticket_price: json.ticket_price,
      age_limit: json.age_limit,
      description: json.description,
      ticket_url: json.ticket_url,
      venue_address: json.venue_address,
      city: json.city,
      state: json.state,
      latitude: json.latitude ? parseFloat(json.latitude) : null,
      longitude: json.longitude ? parseFloat(json.longitude) : null,
      created_at: parseDate(json.created_at),
      genre: json.genre || 'other',
      source: json.source || 'manual',
      poster: ShowPoster.fromJson(json.poster || {}),
      approval_status: json.approval_status || 'approved',
      approval_count: json.approval_count || 0,
      required_approvals: json.required_approvals || 0,
      approved_by: json.approved_by || [],
      flagged_as_spam: json.flagged_as_spam || false,
      report_count: json.report_count || 0,
      reports: json.reports || [],
      reported_by: json.reported_by || [],
      is_hidden: json.is_hidden || false,
      hidden_at: json.hidden_at ? new Date(json.hidden_at) : null,
      total_reports: json.total_reports || 0,
      thanked_by: json.thanked_by || []
    });
  }

  toJson() {
    return {
      band_name: this.band_name,
      venue_name: this.venue_name,
      show_date: this.show_date.toISOString(),
      show_time: this.show_time,
      ticket_price: this.ticket_price,
      age_limit: this.age_limit,
      description: this.description,
      ticket_url: this.ticket_url,
      venue_address: this.venue_address,
      city: this.city,
      state: this.state,
      latitude: this.latitude,
      longitude: this.longitude,
      created_at: this.created_at.toISOString(),
      genre: this.genre,
      source: this.source,
      poster: this.poster.toJson(),
      approval_status: this.approval_status,
      approval_count: this.approval_count,
      required_approvals: this.required_approvals,
      approved_by: this.approved_by,
      flagged_as_spam: this.flagged_as_spam,
      report_count: this.report_count,
      reports: this.reports,
      reported_by: this.reported_by,
      is_hidden: this.is_hidden,
      hidden_at: this.hidden_at ? this.hidden_at.toISOString() : null,
      total_reports: this.total_reports,
      thanked_by: this.thanked_by
    };
  }

  get isUpcoming() {
    return this.show_date > new Date();
  }

  get formattedDate() {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const showDate = new Date(this.show_date.getFullYear(), this.show_date.getMonth(), this.show_date.getDate());

    if (showDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (showDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];
      return `${weekdays[this.show_date.getDay()]}, ${months[this.show_date.getMonth()]} ${this.show_date.getDate()}`;
    }
  }

  get formattedTime() {
    if (!this.show_time) return '';
    
    const timeStr = this.show_time.trim();
    
    // If it already contains AM/PM, return as-is
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }
    
    // Otherwise, assume it's 24-hour format and convert to 12-hour format
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr; // Return as-is if invalid format
    
    const hour = parseInt(parts[0], 10);
    const minute = parts[1];
    
    if (isNaN(hour)) return timeStr; // Return as-is if can't parse hour
    
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
    return `${displayHour}:${minute} ${period}`;
  }

  get timeUntilShow() {
    if (!this.isUpcoming) return 'Past';
    
    const now = new Date();
    const diff = this.show_date.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return `${days} day${days !== 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      return 'Soon';
    }
  }

  // Approval System
  get isApproved() {
    return this.approval_status === 'approved';
  }

  get isPending() {
    return this.approval_status === 'pending';
  }

  get isRejected() {
    return this.approval_status === 'rejected';
  }

  get needsMoreApprovals() {
    return this.isPending && this.approval_count < this.required_approvals;
  }

  get approvalProgress() {
    if (this.required_approvals === 0) return 100;
    return Math.min(100, (this.approval_count / this.required_approvals) * 100);
  }

  // Reporting getters
  get reportThreshold() {
    // Auto-hide threshold based on user activity
    return 5; // Flexible threshold - could be dynamic based on community size
  }

  get shouldBeHidden() {
    return this.total_reports >= this.reportThreshold && !this.is_hidden;
  }

  get canBeReported() {
    return !this.is_hidden && this.approval_status === 'approved';
  }

  get reportStatus() {
    if (this.is_hidden) return 'hidden';
    if (this.total_reports >= this.reportThreshold * 0.8) return 'warning';
    if (this.total_reports > 0) return 'reported';
    return 'clean';
  }
}

export class ShowPoster {
  constructor({ username, type, uid = null, user_level = null, scene_points = 0 }) {
    this.username = username;
    this.type = type;
    this.uid = uid;
    this.user_level = user_level;
    this.scene_points = scene_points;
  }

  static fromJson(json) {
    return new ShowPoster({
      username: json.username || '',
      type: json.type || 'fan',
      uid: json.uid || null,
      user_level: json.user_level || null,
      scene_points: json.scene_points || 0
    });
  }

  toJson() {
    return {
      username: this.username,
      type: this.type,
      uid: this.uid,
      user_level: this.user_level,
      scene_points: this.scene_points
    };
  }
} 